/**
 * Compressió d'imatges al navegador abans de pujar-les (imatges del xat d'una activació).
 *
 * Descodifica la imatge amb `createImageBitmap` (respectant l'orientació EXIF), la redueix
 * perquè el costat llarg no passi de {@link COSTAT_MAX} px i la torna a codificar en JPEG
 * (qualitat {@link QUALITAT_JPEG}). Només es manté PNG si la imatge té transparència i el PNG
 * resultant és petit ({@link MIDA_MAX_PNG}); si no, els píxels transparents es posen sobre fons
 * blanc i es fa JPEG. El resultat només es fa servir si és més petit que l'original. Si la imatge
 * no es pot descodificar (p. ex. HEIC a la majoria de navegadors) o el navegador no té canvas, es
 * retorna l'original tal qual.
 */

export const COSTAT_MAX = 1920;
export const QUALITAT_JPEG = 0.8;
/** Mida màxima d'un PNG amb transparència perquè es mantingui en PNG (si no, passa a JPEG). */
export const MIDA_MAX_PNG = 1024 * 1024;

/** Tipus que poden tenir canal alfa i, per tant, transparència. */
const AMB_ALFA = ['image/png', 'image/webp'];

/** Dimensions reduïdes perquè el costat llarg no passi de `max` (mai no s'amplia). */
export function midaReduida(
  amplada: number,
  alcada: number,
  max = COSTAT_MAX,
): { amplada: number; alcada: number } {
  const factor = Math.min(1, max / Math.max(amplada, alcada));
  return {
    amplada: Math.max(1, Math.round(amplada * factor)),
    alcada: Math.max(1, Math.round(alcada * factor)),
  };
}

/** Nom del fitxer amb l'extensió del tipus nou (`foto.png` → `foto.jpg`). */
export function nomAmbExtensio(nom: string, tipus: string): string {
  const ext = tipus === 'image/png' ? 'png' : 'jpg';
  const base = nom.replace(/\.[^./\\]+$/, '') || 'imatge';
  return `${base}.${ext}`;
}

async function descodifica(f: Blob): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    return await createImageBitmap(f, { imageOrientation: 'from-image' });
  } catch {
    // Navegadors antics que no accepten les opcions: ho tornem a provar sense (l'orientació
    // EXIF hi és igualment la predeterminada).
    try {
      return await createImageBitmap(f);
    } catch {
      return null;
    }
  }
}

function codifica(canvas: HTMLCanvasElement, tipus: string, qualitat?: number) {
  return new Promise<Blob | null>((resol) => {
    try {
      canvas.toBlob((b) => resol(b), tipus, qualitat);
    } catch {
      resol(null);
    }
  });
}

function teTransparencia(ctx: CanvasRenderingContext2D, amplada: number, alcada: number) {
  try {
    const { data } = ctx.getImageData(0, 0, amplada, alcada);
    for (let i = 3; i < data.length; i += 4) if ((data[i] ?? 255) < 255) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Retorna una versió comprimida de la imatge, o l'original si no es pot descodificar o si la
 * versió comprimida no és més petita.
 */
export async function comprimeixImatge(original: File): Promise<File> {
  if (!original.type.startsWith('image/')) return original;
  const bitmap = await descodifica(original);
  if (!bitmap) return original;
  try {
    const { amplada, alcada } = midaReduida(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = amplada;
    canvas.height = alcada;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;

    let resultat: Blob | null = null;
    if (AMB_ALFA.includes(original.type)) {
      ctx.drawImage(bitmap, 0, 0, amplada, alcada);
      if (teTransparencia(ctx, amplada, alcada)) {
        const png = await codifica(canvas, 'image/png');
        if (png && png.size <= MIDA_MAX_PNG) resultat = png;
      }
    }
    if (!resultat) {
      // JPEG no té transparència: fons blanc en lloc del negre que posaria el navegador.
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, amplada, alcada);
      ctx.drawImage(bitmap, 0, 0, amplada, alcada);
      resultat = await codifica(canvas, 'image/jpeg', QUALITAT_JPEG);
    }
    if (!resultat || resultat.size >= original.size) return original;
    const tipus = resultat.type || 'image/jpeg';
    return new File([resultat], nomAmbExtensio(original.name, tipus), {
      type: tipus,
      lastModified: original.lastModified,
    });
  } catch {
    return original;
  } finally {
    bitmap.close?.();
  }
}
